<template>
  <component :is="listTag" class="list-block" :class="`list-${block.style || 'bullet'}`">
    <li
      v-for="(item, idx) in renderedItems"
      :key="idx"
      class="list-item"
    >
      <span v-html="item" />
    </li>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { renderMarkdown } from '../markdown';

interface ListBlock {
  type: 'list';
  style: 'bullet' | 'numbered';
  items: string[];
}

const props = defineProps<{ block: ListBlock }>();

const listTag = computed(() => (props.block.style === 'numbered' ? 'ol' : 'ul'));

const renderedItems = computed(() => {
  return props.block.items.map((raw) => {
    let text = raw.trim();
    text = text.replace(/^[-*+]\s+/, '');
    text = text.replace(/^\d+[.)]\s+/, '');
    const html = renderMarkdown(text);
    return html.replace(/^<p>/, '').replace(/<\/p>\s*$/, '');
  });
});
</script>

<style scoped lang="scss">
.list-block {
  margin: 16px 0;
  padding-left: 8px;
  color: var(--oa-text-primary);
  line-height: 1.7;
  font-size: var(--oa-text-base);
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style: none;
}

.list-block.list-numbered {
  counter-reset: list-counter;
}

.list-item {
  position: relative;
  padding-left: 28px;
  display: block;
}

.list-bullet .list-item::before {
  content: '';
  position: absolute;
  left: 8px;
  top: 0.65em;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--oa-gold-full);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--oa-gold-full) 14%, transparent);
}

.list-numbered {
  counter-reset: list-counter;
}

.list-numbered .list-item {
  counter-increment: list-counter;
  padding-left: 36px;
}

.list-numbered .list-item::before {
  content: counter(list-counter);
  position: absolute;
  left: 0;
  top: 0.05em;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--oa-text-xs);
  font-weight: 700;
  color: var(--oa-gold-full);
  background: color-mix(in srgb, var(--oa-gold-full) 12%, transparent);
  border-radius: 50%;
  line-height: 1;
}

:deep(strong) {
  color: var(--oa-text-primary);
  font-weight: 700;
}

:deep(em) {
  font-style: italic;
}

:deep(code) {
  background: var(--oa-bg-3);
  padding: 1px 5px;
  border-radius: var(--oa-radius-sm);
  font-family: var(--oa-font-mono);
  font-size: 0.85em;
}

:deep(a) {
  color: var(--oa-gold-full);
  text-decoration: underline;
}
</style>
